import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../shared/config";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Bell, BellOff, Trash2, Smartphone, Monitor, Tablet, ShieldCheck, Send, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;

const detectDevice = () => {
  const ua = navigator.userAgent;
  let browser = 'Navigateur', os = 'Appareil', deviceName = '';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';
  if (ua.includes('iPhone')) { os = 'iOS'; deviceName = 'iPhone'; }
  else if (ua.includes('iPad') || isIOS()) { os = 'iPadOS'; deviceName = 'iPad'; }
  else if (ua.includes('Android')) { os = 'Android'; deviceName = 'Android'; }
  else if (ua.includes('Mac')) { os = 'macOS'; deviceName = 'Mac'; }
  else if (ua.includes('Windows')) { os = 'Windows'; deviceName = 'PC'; }
  else { os = 'Autre'; deviceName = 'Appareil'; }
  return { ua, browser, os, deviceName: `${deviceName} - ${browser}` };
};

const urlBase64ToUint8Array = (value) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
};

const getSWRegistration = async () => {
  let reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) {
    reg = await navigator.serviceWorker.register('/service-worker.js', { updateViaCache: 'none' });
  }
  if (reg.installing || reg.waiting) {
    const sw = reg.installing || reg.waiting;
    await new Promise((resolve) => {
      sw.addEventListener('statechange', function () { if (this.state === 'activated') resolve(); });
      if (sw.state === 'activated') resolve();
    });
  }
  return reg;
};

const PushNotificationManager = () => {
  const { user, centreActif } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (user) {
      fetchDevices();
      autoRefreshSubscription();
    }
  }, [user]);

  // Le service worker signale un renouvellement d'abonnement → on ré-enregistre côté serveur
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMsg = (e) => { if (e.data?.type === 'PUSH_RESUBSCRIBED') registerSubscription(e.data.subscription, true); };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, []);

  const registerSubscription = async (subJson, silent = false) => {
    if (!subJson?.endpoint) return;
    const { ua, browser, os, deviceName } = detectDevice();
    await axios.post(`${API}/notifications/subscribe`, {
      subscription: subJson, centre_id: centreActif?.id || null,
      device_info: { userAgent: ua, platform: navigator.platform, deviceName, browser, os }
    });
    await fetchDevices();
    if (!silent) toast.success(`Notifications activées sur ${deviceName}`);
  };

  const autoRefreshSubscription = async () => {
    try {
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        // Permission accordée mais abonnement perdu (app réinstallée, navigateur nettoyé) → on se réabonne silencieusement
        const { data: keyData } = await axios.get(`${API}/notifications/vapid-public-key`);
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(keyData.publicKey) });
        await registerSubscription(sub.toJSON(), true);
        return;
      }
      const { data } = await axios.post(`${API}/notifications/refresh-token`, { subscription: sub.toJSON() });
      if (data?.reregister) await registerSubscription(sub.toJSON(), true);
    } catch (e) { console.log('Auto-refresh subscription skipped:', e.message); }
  };

  const sendTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/notifications/test-me`);
      setTestResult(data);
      if (data.nb === 0) toast.error('Aucun appareil enregistré');
      else if (data.ok) toast.success('Notification de test envoyée — vérifiez votre téléphone');
      else toast.error('Échec de l\'envoi sur tous les appareils');
      await fetchDevices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi du test'); }
    finally { setTesting(false); }
  };

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/notifications/devices`);
      const list = res.data?.devices || [];
      setDevices(list);
      setEnabled(list.length > 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const getDeviceIcon = (device) => {
    const os = device.os || device.device_name || '';
    if (os.includes('iPhone') || os.includes('Android') || os.includes('iOS')) return <Smartphone className="h-4 w-4 text-gray-500" />;
    if (os.includes('iPad') || os.includes('Tablet')) return <Tablet className="h-4 w-4 text-gray-500" />;
    return <Monitor className="h-4 w-4 text-gray-500" />;
  };

  const subscribeToPush = async () => {
    setActivating(true);
    try {
      if (isIOS() && !isStandalone()) {
        toast.error("Sur iPhone/iPad, ajoutez d'abord OphtaPilot à l'écran d'accueil (Partager → Sur l'écran d'accueil), puis activez les notifications depuis l'app installée.", { duration: 8000 });
        setActivating(false);
        return;
      }
      if ('Notification' in window && Notification.permission === 'default') {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          toast.error('Vous devez autoriser les notifications');
          setActivating(false);
          return;
        }
      }
      if (Notification.permission !== 'granted') {
        toast.error('Notifications bloquées par le navigateur');
        setActivating(false);
        return;
      }

      const { data: keyData } = await axios.get(`${API}/notifications/vapid-public-key`);
      const reg = await getSWRegistration();

      let sub = await reg.pushManager.getSubscription();
      if (sub) {
        // Ré-abonner si la clé VAPID a changé
        try {
          const currentKey = sub.options?.applicationServerKey;
          if (currentKey) {
            const newKey = urlBase64ToUint8Array(keyData.publicKey);
            const oldKey = new Uint8Array(currentKey);
            if (oldKey.length !== newKey.length || !oldKey.every((b, i) => b === newKey[i])) {
              await sub.unsubscribe();
              sub = null;
            }
          }
        } catch { /* garder la souscription existante */ }
      }
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyData.publicKey),
        });
      }

      await registerSubscription(sub.toJSON());
    } catch (err) {
      toast.error('Erreur: ' + (err.response?.data?.detail || err.message));
    } finally { setActivating(false); }
  };

  const removeDevice = async (deviceId) => {
    try {
      await axios.delete(`${API}/notifications/devices/${deviceId}`);
      toast.success('Appareil retiré');
      await fetchDevices();
    } catch { toast.error('Erreur'); }
  };

  const disableAll = async () => {
    try {
      for (const d of devices) {
        await axios.delete(`${API}/notifications/devices/${d.device_id}`).catch(() => {});
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      } catch { /* ignore */ }
      setDevices([]);
      setEnabled(false);
      toast.success('Notifications désactivées');
    } catch { toast.error('Erreur'); }
  };

  const handleToggle = async (value) => {
    if (value) {
      await subscribeToPush();
    } else {
      await disableAll();
    }
  };

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-500">
        Votre navigateur ne supporte pas les notifications push.
      </div>
    );
  }

  if (loading) return null;

  return (
    <div className="space-y-3" data-testid="push-notification-manager">
      {/* Toggle principal */}
      <div className="flex items-center gap-3">
        <Switch
          checked={enabled}
          onCheckedChange={handleToggle}
          disabled={activating}
          data-testid="push-toggle"
        />
        <div className="flex items-center gap-2">
          {enabled ? <Bell className="h-4 w-4 text-green-600" /> : <BellOff className="h-4 w-4 text-gray-400" />}
          <span className="text-sm font-medium text-gray-700">Notifications push</span>
        </div>
      </div>

      <p className="flex items-center gap-1.5 text-[11px] text-gray-400">
        <ShieldCheck className="h-3 w-3 text-emerald-500" />
        Chiffrement de bout en bout (Web Push) — conforme RGPD
      </p>

      {isIOS() && !isStandalone() && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="push-ios-hint">
          <b>iPhone / iPad :</b> les notifications ne fonctionnent que depuis l'app installée sur l'écran d'accueil (Safari → Partager → « Sur l'écran d'accueil », iOS 16.4+).
        </p>
      )}

      {/* Permission bloquée */}
      {'Notification' in window && Notification.permission === 'denied' && (
        <p className="text-xs text-red-500">
          Notifications bloquées. Allez dans les paramètres de votre navigateur pour les réactiver.
        </p>
      )}

      {/* Liste des appareils */}
      {enabled && devices.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-gray-500">{devices.length} appareil{devices.length > 1 ? 's' : ''} relié{devices.length > 1 ? 's' : ''}</p>
          {devices.map((d, i) => (
            <div key={d.device_id || i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                {getDeviceIcon(d)}
                <div>
                  <p className="text-sm text-gray-800">{d.device_name || 'Appareil'}</p>
                  <p className="text-[10px] text-gray-400 flex items-center gap-1.5">
                    <span>{d.registered_at ? new Date(d.registered_at).toLocaleDateString('fr-FR') : ''}</span>
                    {d.last_push_status === 'ok' && <span className="inline-flex items-center gap-0.5 text-emerald-600" data-testid={`device-status-${i}`}><CheckCircle2 className="h-3 w-3" /> dernier envoi OK {d.last_push_at ? new Date(d.last_push_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>}
                    {d.last_push_status === 'erreur' && <span className="inline-flex items-center gap-0.5 text-red-600" title={d.last_push_error || ''} data-testid={`device-status-${i}`}><AlertTriangle className="h-3 w-3" /> dernier envoi en erreur ({d.last_push_error || '?'})</span>}
                  </p>
                </div>
              </div>
              <button onClick={() => removeDevice(d.device_id)} className="text-slate-500 hover:text-red-500 transition-colors" data-testid={`remove-device-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={sendTest} disabled={testing} data-testid="push-test-btn">
              {testing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} M'envoyer une notification test
            </Button>
            <Button variant="ghost" size="sm" className="text-xs text-gray-500 hover:text-gray-700" onClick={subscribeToPush} disabled={activating}>
              + Ajouter cet appareil
            </Button>
          </div>
          {testResult && (
            <div className="text-[11px] space-y-0.5" data-testid="push-test-result">
              {testResult.results.map((r, i) => (
                <div key={i} className={r.ok ? 'text-emerald-700' : 'text-red-600'}>{r.ok ? '✔' : '✖'} {r.device_name || 'Appareil'} {r.ok ? '— envoyé' : `— ${r.error === 'expired' ? 'abonnement expiré (retiré, réactivez sur cet appareil)' : (r.error || 'échec')}`}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PushNotificationManager;
